'use client'

/*
 * eslint-disable -- o AppLink ja encapsula WLink e WLinkButton; o divider e
 * uma regra decorativa de 1px, nao texto auxiliar.
 */
/* eslint-disable @alice-health/w-link, @alice-health/w-helper */

import { useEffect, useRef, useState } from 'react'
import { Link } from '@/global/components/Link'
import { useDrawer } from '@/global/hooks'
import { Logo } from '@/global/components/Logo'
import {
  FOR_BROKERS_URL,
  HOME_URL,
  SIMULATOR_URL
} from '@/global/constants/routes'
import {
  BROKERS_BUTTON_LABEL,
  START_SIMULATION_BUTTON_LABEL
} from '@/global/constants/general'
import classNames from 'classnames'
import { zIndex } from '@/global/constants/z-index'
import { MENU_ANIMATION_MS } from './Navbar.constants'
import { NavbarDrawer } from './NavbarDrawer'
import { NavbarHamburger } from './NavbarHamburger'
import { NavbarMenu } from './NavbarMenu'
import {
  menuDrawerId,
  sendButtonClickedAnalyticEvent,
  sendStartSimulatorAnalyticEvent
} from './Navbar.helpers'

const Navbar = () => {
  const { isOpen, open, close } = useDrawer(menuDrawerId)
  const [isClosing, setIsClosing] = useState(false)
  const closeTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined
  )

  useEffect(() => () => clearTimeout(closeTimeout.current), [])

  /*
   * O `Drawer` monta o painel apenas enquanto o store diz que esta aberto,
   * entao o `close` espera a animacao de saida terminar. Cliques durante esses
   * 300ms sao ignorados, senao cada um agendaria um fechamento novo.
   */
  const toggleMenu = () => {
    if (isClosing) return

    if (!isOpen) {
      open()
      return
    }

    setIsClosing(true)

    closeTimeout.current = setTimeout(() => {
      setIsClosing(false)
      close()
    }, MENU_ANIMATION_MS)
  }

  /*
   * `absolute`, nao `fixed`: o header rola com a pagina e sai da tela, e a barra
   * de filtros e que assume o topo. Continua fora do fluxo, entao o
   * `mt-[var(--app-header-height)]` de quem vem abaixo segue valendo.
   *
   * Com o menu aberto ele volta a ser `fixed`, como em producao: o X e o logo
   * continuam no lugar enquanto a pagina rola por tras. O vidro acompanha.
   */
  const position = isOpen ? 'fixed' : 'absolute'

  return (
    <>
      {/*
       * Vidro do header, como o `.n-navbar_blur` de producao: branco a 40% com
       * `blur(44px)`, sempre ligado, no lugar de um fundo opaco. No topo da
       * pagina nao ha nada atras da barra e ele le como branco; ao rolar, o
       * conteudo passa por tras e aparece desfocado.
       *
       * Irmao da barra, e nao filho: a barra cria contexto de empilhamento,
       * entao um vidro dentro dela ficaria sempre na frente do painel do menu.
       */}
      <div
        aria-hidden
        data-testid='navbar-blur'
        className={classNames(
          position,
          'pointer-events-none left-0 top-[0] h-[var(--app-header-height)] w-full',
          'bg-gl-color-transparent-white-40 backdrop-blur-[44px]',
          zIndex.global.navbarBlur
        )}
      />

      <div
        data-testid='navbar'
        className={classNames(
          position,
          'top-[0] flex w-full flex-row items-center justify-between',
          'h-[var(--app-header-height)]',
          'pl-gl-spacing-01 pr-gl-spacing-05 lg:px-gl-spacing-06',
          zIndex.global.navbar
        )}
      >
        <div className='flex items-center gap-gl-spacing-01 p-gl-spacing-01 lg:gap-[6px] lg:p-0'>
          <NavbarHamburger
            isOpen={isOpen}
            onToggle={toggleMenu}
          />

          {/*
           * O divisor desaparece com o menu aberto, como em producao: ao lado do
           * X ele ficaria a um pixel do braco da direita. Ele perde a opacidade
           * em vez de sair do DOM, senao o logo andaria 7px para a esquerda e a
           * troca do hamburger pelo X deixaria de parecer o mesmo elemento.
           */}
          <span
            className={classNames(
              'h-[20px] w-px bg-gl-color-transparent-black-15 lg:h-[24px]',
              isOpen && 'opacity-0'
            )}
          />

          <Link
            as='link'
            href={HOME_URL}
            className='pl-gl-spacing-03'
          >
            <Logo size='small' />
          </Link>
        </div>

        <NavbarMenu />

        <div className='navbar-actions-theme flex items-center gap-gl-spacing-01'>
          <div className='hidden lg:block'>
            <Link
              as='button'
              size='large'
              variant='white'
              href={FOR_BROKERS_URL}
              onClick={() =>
                sendButtonClickedAnalyticEvent(BROKERS_BUTTON_LABEL)
              }
            >
              {BROKERS_BUTTON_LABEL}
            </Link>
          </div>

          <Link
            as='button'
            size='large'
            variant='cta'
            href={SIMULATOR_URL}
            onClick={() =>
              sendStartSimulatorAnalyticEvent(START_SIMULATION_BUTTON_LABEL)
            }
          >
            {START_SIMULATION_BUTTON_LABEL}
          </Link>
        </div>

        <NavbarDrawer
          isClosing={isClosing}
          onClose={toggleMenu}
        />
      </div>
    </>
  )
}

export { Navbar }
